import { useMemo } from "react";

import { COUNTRIES } from "./countries";
import { Select } from "./select";
import type { SelectOption } from "./select";

/**
 * The three fields nobody can type correctly from memory.
 *
 * ── Why these exist ─────────────────────────────────────────────────────────
 *
 * All three were free-text inputs, and all three are validated strictly by the
 * server: an IANA zone, an ISO 4217 code and an ISO 3166-1 alpha-2 code in
 * capitals. So the settings screen answered a perfectly reasonable attempt with
 * three refusals at once — the server being right and the form being useless.
 * **A field whose only legal values are a known finite list should not be a
 * text box**; the validation was never the problem.
 *
 * ── They are now `Select`, not `<select>` ───────────────────────────────────
 *
 * A native select is uneditable and remembers its value, but cannot be
 * searched, and these are the three longest lists in the product — four hundred
 * zones, three hundred currencies, two hundred countries. See `select.tsx`.
 *
 * ── Where the lists come from ───────────────────────────────────────────────
 *
 * Timezones and currencies come from `Intl.supportedValuesOf`, so the browser's
 * own database is the source and nothing here can go stale. Countries need a
 * table because no `Intl` call enumerates them, and that table carries the
 * dialling codes anyway — see `countries.ts` for why an international standard
 * is not "clinic data hardcoded".
 */

// ── Timezone ────────────────────────────────────────────────────────────────

function allTimezones(): string[] {
  try {
    const supported = Intl.supportedValuesOf("timeZone");
    /**
     * **`UTC` is not in that list, and it is a real answer.**
     *
     * `supportedValuesOf` returns 418 region/city zones and no `UTC`, no
     * `Etc/UTC` — so the first version of this picker could not offer the one
     * zone both seeded clinics actually use.
     */
    if (supported.length > 0) return ["UTC", ...supported];
  } catch {
    // Falls through.
  }
  return ["UTC"];
}

/**
 * A zone's current offset, as the minutes east of UTC and as `GMT+3`.
 *
 * **Computed rather than stored, and that is the whole argument for keeping
 * real zones.** The client asked for a list reading `GMT+3, GMT+2, …`; a fixed
 * offset cannot follow daylight saving, so a practice in a DST country would be
 * an hour out for half the year — and appointment times are the last thing that
 * should drift. Naming the zone and *showing* its offset gives the list he
 * asked for while the stored value stays something that knows about summer.
 *
 * The offset is today's. A zone that changes in March will read differently in
 * April, which is correct: it is a label, not the value.
 */
function offsetOf(zone: string): { minutes: number; label: string } {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());

    const raw = parts.find((part) => part.type === "timeZoneName")?.value ?? "GMT";

    // `shortOffset` gives "GMT", "GMT+3" or "GMT+5:30". Bare "GMT" is zero.
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(raw);
    if (match === null) return { minutes: 0, label: "GMT+0" };

    const sign = match[1] === "-" ? -1 : 1;
    const hours = Number(match[2] ?? "0");
    const mins = Number(match[3] ?? "0");
    const minutes = sign * (hours * 60 + mins);

    // Normalised so every row reads the same width-ish: GMT+3, GMT+5:30.
    const label = `GMT${sign < 0 ? "-" : "+"}${String(hours)}${mins === 0 ? "" : `:${String(mins).padStart(2, "0")}`}`;
    return { minutes, label };
  } catch {
    return { minutes: 0, label: "GMT+0" };
  }
}

/** `Asia/Beirut` → `Beirut`, `America/Argentina/Salta` → `Argentina · Salta`. */
function cityOf(zone: string): string {
  if (!zone.includes("/")) return zone;
  return zone
    .slice(zone.indexOf("/") + 1)
    .replace(/_/g, " ")
    .replace(/\//g, " · ");
}

export function TimezonePicker({
  id,
  label,
  value,
  onChange,
  testId,
  note,
  invalid,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  testId: string;
  note?: string;
  invalid?: boolean;
  /** Draws the red star (see `ui/required-mark.tsx`). */
  required?: boolean;
}): React.JSX.Element {
  const options = useMemo<SelectOption[]>(() => {
    const all = allTimezones();
    // The clinic's current zone is always selectable, even if this browser's
    // database does not list it — otherwise opening the form would silently
    // change a value nobody touched.
    if (value !== "" && !all.includes(value)) all.push(value);

    return (
      all
        .map((zone) => {
          const { minutes, label: gmt } = offsetOf(zone);
          return {
            zone,
            minutes,
            option: {
              value: zone,
              // The client's requested shape, with the zone kept visible so two
              // cities on the same offset are still tellable apart.
              label: `(${gmt}) ${cityOf(zone)}`,
              // Searchable by IANA name, by city and by offset, because people
              // arrive with any of the three.
              keywords: `${zone} ${gmt}`,
              hint: zone.includes("/") ? zone.slice(0, zone.indexOf("/")) : undefined,
            } satisfies SelectOption,
          };
        })
        // Sorted west to east, then by name — the order an offset list is read
        // in. Alphabetical by city would scatter GMT+3 across the whole list.
        .sort((a, b) => a.minutes - b.minutes || a.zone.localeCompare(b.zone))
        .map((entry) => entry.option)
    );
  }, [value]);

  return (
    <Select
      id={id}
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      testId={testId}
      note={note}
      invalid={invalid}
      required={required}
      placeholder="Choose a timezone…"
    />
  );
}

// ── Currency ────────────────────────────────────────────────────────────────

function allCurrencies(): string[] {
  try {
    const supported = Intl.supportedValuesOf("currency");
    if (supported.length > 0) return [...supported];
  } catch {
    // Falls through.
  }
  return ["USD", "EUR", "GBP"];
}

const currencyNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: "currency" });
  } catch {
    return null;
  }
})();

export function CurrencyPicker({
  id,
  label,
  value,
  onChange,
  testId,
  note,
  invalid,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  testId: string;
  note?: string;
  invalid?: boolean;
  /** Draws the red star (see `ui/required-mark.tsx`). */
  required?: boolean;
}): React.JSX.Element {
  const options = useMemo<SelectOption[]>(() => {
    const all = allCurrencies();
    if (value !== "" && !all.includes(value)) all.push(value);

    return all
      .map((code) => {
        let name = code;
        try {
          name = currencyNames?.of(code) ?? code;
        } catch {
          // Keep the code.
        }
        // **The code leads.** It is what the clinic thinks in and what the
        // server stores; the name is the thing that makes it findable.
        return {
          value: code,
          label: name === code ? code : `${code} — ${name}`,
          keywords: name,
        } satisfies SelectOption;
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [value]);

  return (
    <Select
      id={id}
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      testId={testId}
      note={note}
      invalid={invalid}
      required={required}
      placeholder="Choose a currency…"
    />
  );
}

// ── Country ─────────────────────────────────────────────────────────────────

export function CountryPicker({
  id,
  label,
  value,
  onChange,
  testId,
  note,
  invalid,
  required,
  /**
   * Whether "not set" is offered. It is the right answer for a clinic serving
   * two countries, and the wrong one where a country decides a dialling code —
   * see `#4`, where a location's country fills the phone field's prefix.
   */
  allowUnset = true,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  testId: string;
  note?: string;
  invalid?: boolean;
  /** Draws the red star (see `ui/required-mark.tsx`). */
  required?: boolean;
  allowUnset?: boolean;
}): React.JSX.Element {
  const options = useMemo<SelectOption[]>(
    () =>
      // Already sorted by name in `countries.ts`, which is the one country
      // table in the application.
      COUNTRIES.map((country) => ({
        value: country.iso2,
        label: country.name,
        keywords: `${country.iso2} ${country.dialCode}`,
        hint: country.dialCode,
      })),
    [],
  );

  return (
    <Select
      id={id}
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      testId={testId}
      note={note}
      invalid={invalid}
      required={required}
      placeholder="Choose a country…"
      emptyLabel={allowUnset ? "Not set" : undefined}
    />
  );
}
